import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  getLiveSessionReports,
  LiveSessionReportsResult,
  blockLiveAccess,
  suspendLiveAccess,
} from '../services/liveAccessApi';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const LiveSessionReportsDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<LiveSessionReportsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [blockNote, setBlockNote] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  const load = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    try {
      const response = await getLiveSessionReports(sessionId);
      setData(response);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load live reports');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleBlock = async () => {
    const hostUserId = data?.session?.HostUserID;
    if (!hostUserId) return;
    if (!blockNote.trim()) { toast.error('A reason is required to block'); return; }
    setActionLoading('BLOCKED');
    try {
      await blockLiveAccess(hostUserId, blockNote.trim());
      toast.success('User blocked from live access');
      setBlockNote('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to block');
    } finally {
      setActionLoading('');
    }
  };

  const handleSuspend = async () => {
    const hostUserId = data?.session?.HostUserID;
    if (!hostUserId) return;
    if (!window.confirm('Suspend this user\'s live access? This will terminate any active session immediately.')) return;
    setActionLoading('SUSPENDED');
    try {
      await suspendLiveAccess(hostUserId);
      toast.success('User suspended from live access');
    } catch (err: any) {
      toast.error(err.message || 'Failed to suspend');
    } finally {
      setActionLoading('');
    }
  };

  const reportsByReporter = useMemo(() => {
    const allReports = data?.reports ?? [];
    const map = new Map<string, typeof allReports>();
    for (const r of allReports) {
      const key = r.ReporterUserID || 'unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return Array.from(map.entries())
      .map(([, reps]) => ({
        reporterUserId: reps[0]?.ReporterUserID ?? 'unknown',
        reporterUserName: reps[0]?.ReporterUserName ?? null,
        reporterReports: reps,
      }))
      .sort((a, b) => b.reporterReports.length - a.reporterReports.length);
  }, [data?.reports]);

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='flex items-center justify-center py-32'>
          <div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
        </div>
      </div>
    );
  }

  if (!data?.session) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 py-16 text-center'>
          <p className='text-gray-500'>Live report details not found.</p>
          <button
            onClick={() => navigate('/live-access/reports')}
            className='mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'
          >
            Back to Live Reports
          </button>
        </div>
      </div>
    );
  }

  const { session, host, reportCount, reporterCount, lastReportedAt } = data;
  const displayTitle = session.Title || 'Untitled live';
  const hostDisplayName = host?.Name || host?.UserName || session.HostUserID;

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <button
          onClick={() => navigate('/live-access/reports')}
          className='mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900'
        >
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
          Back to Live Reports
        </button>

        <div className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
          <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
            <div className='min-w-0'>
              <h1 className='text-2xl font-bold text-gray-900'>{displayTitle}</h1>
              <p className='mt-1 text-sm text-gray-500'>Host: {hostDisplayName}</p>
              <p className='mt-1 text-xs font-mono text-gray-400'>{session.SessionID}</p>
            </div>
            <div className='flex flex-wrap gap-3'>
              <div className='rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center'>
                <p className='text-2xl font-bold text-rose-600'>{reportCount}</p>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Reports</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center'>
                <p className='text-2xl font-bold text-blue-600'>{reporterCount}</p>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Reporters</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center'>
                <p className='text-sm font-bold text-gray-900'>{formatDate(lastReportedAt)}</p>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Last Reported</p>
              </div>
            </div>
          </div>

          <div className='mt-5 grid gap-4 lg:grid-cols-[320px,1fr]'>
            <div className='overflow-hidden rounded-xl border border-gray-200 bg-gray-100'>
              {session.recordingUrl ? (
                <video
                  src={session.recordingUrl}
                  poster={session.CoverUrl || undefined}
                  controls
                  className='h-64 w-full bg-black object-contain'
                />
              ) : session.CoverUrl ? (
                <img src={session.CoverUrl} alt={displayTitle} className='h-64 w-full object-cover' />
              ) : (
                <div className='flex h-64 items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black px-6 text-center text-sm text-gray-300'>
                  No cover or recording available for this session.
                </div>
              )}
            </div>

            <div className='grid gap-3 sm:grid-cols-2'>
              <div className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Lifecycle</p>
                <p className='mt-2 text-sm font-medium text-gray-900'>{session.LifecycleStatus}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Publish Status</p>
                <p className='mt-2 text-sm font-medium text-gray-900'>{session.PublishStatus || 'N/A'}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Started</p>
                <p className='mt-2 text-sm font-medium text-gray-900'>{formatDate(session.StartedAt)}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Ended</p>
                <p className='mt-2 text-sm font-medium text-gray-900'>{formatDate(session.EndedAt)}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-gray-50 p-4 sm:col-span-2'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Policy</p>
                <p className='mt-2 text-sm font-medium text-gray-900'>
                  Chat: {session.Policy?.chatEnabled ? 'Enabled' : 'Disabled'} | Followers only:{' '}
                  {session.Policy?.followersOnlyChat ? 'Yes' : 'No'} | Slow mode: {session.Policy?.slowModeSeconds ?? 0}s
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Host moderation actions */}
        <div className='mt-6 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
          <div className='border-b border-gray-200 bg-gray-50 px-5 py-3 flex items-center gap-3'>
            <h2 className='text-sm font-semibold uppercase tracking-wide text-gray-600'>Host Moderation</h2>
            <button
              onClick={() => navigate(`/live-access/${encodeURIComponent(session.HostUserID)}`)}
              className='ml-auto text-xs font-medium text-gray-500 hover:text-gray-800 underline underline-offset-2'
            >
              View Application →
            </button>
          </div>
          <div className='grid gap-0 divide-y divide-gray-100 lg:divide-y-0 lg:divide-x lg:grid-cols-2'>
            <div className='p-5'>
              <p className='mb-1 text-sm font-semibold text-orange-800'>Block Access</p>
              <p className='mb-3 text-xs text-orange-600'>Blocked users cannot reapply. Reapply attempts tracked.</p>
              <div className='space-y-2'>
                <textarea
                  value={blockNote}
                  onChange={(e) => setBlockNote(e.target.value)}
                  rows={2}
                  placeholder='Reason for blocking (required)…'
                  className='w-full rounded-lg border border-orange-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-orange-500 focus:outline-none'
                />
                <button
                  onClick={handleBlock}
                  disabled={Boolean(actionLoading)}
                  className='w-full rounded-lg bg-orange-600 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60'
                >
                  {actionLoading === 'BLOCKED' ? 'Blocking…' : 'Block Access'}
                </button>
              </div>
            </div>
            <div className='p-5'>
              <p className='mb-1 text-sm font-semibold text-red-800'>Suspend Access</p>
              <p className='mb-3 text-xs text-red-600'>Terminates any active session immediately.</p>
              <button
                onClick={handleSuspend}
                disabled={Boolean(actionLoading)}
                className='w-full rounded-lg bg-red-700 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-60'
              >
                {actionLoading === 'SUSPENDED' ? 'Suspending…' : 'Suspend Permanently'}
              </button>
            </div>
          </div>
        </div>

        <div className='mt-6 space-y-4'>
          {reportsByReporter.length === 0 ? (
            <div className='rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500 shadow-sm'>
              No reports have been recorded for this session.
            </div>
          ) : (
            reportsByReporter.map(({ reporterUserId, reporterUserName, reporterReports }) => (
              <div key={reporterUserId} className='rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
                {/* Reporter header — shown once per unique reporter */}
                <div className='flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-6 py-4'>
                  <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-100 text-sm font-bold text-rose-700'>
                    {(reporterUserName || reporterUserId || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='font-semibold text-gray-900'>
                      {reporterUserName ? `@${reporterUserName}` : (reporterUserId || 'Unknown')}
                    </p>
                    <p className='text-xs font-mono text-gray-400'>{reporterUserId || 'N/A'}</p>
                  </div>
                  <span className='shrink-0 inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-2.5 py-1 text-xs font-bold text-white'>
                    {reporterReports.length} {reporterReports.length === 1 ? 'report' : 'reports'}
                  </span>
                </div>

                {/* Individual reports from this reporter */}
                <div className='divide-y divide-gray-100'>
                  {reporterReports.map((report) => (
                    <div key={report.ReportID || `${reporterUserId}-${report.createdAt}`} className='p-6'>
                      <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
                        <div className='flex flex-wrap gap-2'>
                          <span className='inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700'>
                            {report.ReasonLabel || report.ReasonCode || 'Report'}
                          </span>
                          {report.ReportedUserID ? (
                            <span className='inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800'>
                              About {report.ReportedUserName || report.ReportedUserID}
                              {report.ReportedUserRole ? ` (${report.ReportedUserRole.toLowerCase()})` : ''}
                            </span>
                          ) : (
                            <span className='inline-flex rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700'>
                              About the host
                            </span>
                          )}
                          {report.ReporterRole && report.ReporterRole !== 'VIEWER' && (
                            <span className='inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700'>
                              Reported by {report.ReporterRole.toLowerCase()}
                            </span>
                          )}
                          {report.BlockHost && (
                            <span className='inline-flex rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700'>
                              Block Host
                            </span>
                          )}
                        </div>
                        <span className='text-xs text-gray-400'>{formatDate(report.createdAt)}</span>
                      </div>

                      <div className='grid gap-4 lg:grid-cols-[1fr,320px]'>
                        <div className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
                          <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Report Details</p>
                          <p className='mt-2 text-sm leading-6 text-gray-800'>{report.Details || 'No extra details provided.'}</p>
                        </div>
                        <div className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
                          <p className='text-xs font-semibold uppercase tracking-wide text-gray-400'>Metadata</p>
                          <div className='mt-2 space-y-2 text-sm text-gray-700'>
                            <p><span className='font-semibold text-gray-900'>Report ID:</span> {report.ReportID}</p>
                            <p><span className='font-semibold text-gray-900'>Session ID:</span> {report.SessionID}</p>
                            <p><span className='font-semibold text-gray-900'>Host ID:</span> {report.HostUserID}</p>
                            {report.ReportedUserID && (
                              <p><span className='font-semibold text-gray-900'>Reported user:</span> {report.ReportedUserID}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {report.ScreenshotUrls.length > 0 && (
                        <div className='mt-4'>
                          <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400'>Screenshots</p>
                          <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
                            {report.ScreenshotUrls.map((url) => (
                              <a key={url} href={url} target='_blank' rel='noreferrer'
                                className='overflow-hidden rounded-xl border border-gray-200 bg-white transition-transform hover:-translate-y-0.5'>
                                <img src={url} alt='Report screenshot' className='h-48 w-full object-cover' />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveSessionReportsDetail;
